import { useEffect, useMemo, useRef, useState } from "react";
import {
  emptyScene,
  sceneFromOnDisk,
  type BoardScene,
} from "@/lib/canvas/board-schema";
import { TacticalBoardView } from "./TacticalBoardView";

export type TacticalBoardThumbnailProps = {
  /** Raw `tactical_board_json` from a note, or a pre-parsed scene. */
  raw?: unknown;
  scene?: BoardScene | null;
  width: number;
  height?: number;
  className: string;
  hideLabels?: boolean;
  /** `field` renders an empty pitch; `label` renders text; `none`
   * returns null when there is no valid board content. */
  fallback?: "field" | "label" | "none";
  fallbackLabel?: string;
  testId?: string;
  /** Measure the wrapper and fill its available width. The numeric width is
   * retained as the pre-measurement fallback for tests and first paint. */
  responsive?: boolean;
  /** With responsive width, also use the wrapper's stretched height. */
  responsiveHeight?: boolean;
};

export function TacticalBoardThumbnail({
  raw,
  scene,
  width,
  height,
  className,
  hideLabels = true,
  fallback = "label",
  fallbackLabel = "No board",
  testId = "tactical-board-thumbnail",
  responsive = false,
  responsiveHeight = false,
}: TacticalBoardThumbnailProps) {
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState(width);
  const [measuredHeight, setMeasuredHeight] = useState(height);

  useEffect(() => {
    if (!responsive || typeof ResizeObserver === "undefined") return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const update = (nextWidth: number, nextHeight: number) => {
      if (nextWidth > 0) setMeasuredWidth(Math.round(nextWidth));
      if (responsiveHeight && nextHeight > 0) {
        setMeasuredHeight(Math.round(nextHeight));
      }
    };
    const initialRect = wrapper.getBoundingClientRect();
    update(initialRect.width, initialRect.height);
    const observer = new ResizeObserver((entries) => {
      update(
        entries[0]?.contentRect.width ?? 0,
        entries[0]?.contentRect.height ?? 0,
      );
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [responsive, responsiveHeight]);

  const renderWidth = responsive ? measuredWidth : width;
  const renderHeight = responsiveHeight
    ? measuredHeight
    : height == null
      ? undefined
      : height * (renderWidth / width);
  const parsedScene = useMemo(() => {
    if (scene) return scene;
    if (raw == null) return null;
    const result = sceneFromOnDisk(raw);
    return result.ok ? result.value : null;
  }, [raw, scene]);

  const hasContent =
    parsedScene !== null &&
    (parsedScene.tokens.length > 0 || parsedScene.shapes.length > 0);
  const displayScene = hasContent
    ? parsedScene
    : fallback === "field"
      ? (parsedScene ?? emptyScene())
      : null;

  if (!displayScene && fallback === "none") return null;

  return (
    <span
      ref={wrapperRef}
      aria-hidden="true"
      data-testid={testId}
      className={className}
    >
      {displayScene ? (
        <TacticalBoardView
          appearance="navy"
          scene={displayScene}
          width={renderWidth}
          height={renderHeight}
          hideLabels={hideLabels}
          passThrough
        />
      ) : (
        <span className="px-2 text-center font-mono text-[10px] text-ink-muted">
          {fallbackLabel}
        </span>
      )}
    </span>
  );
}

export function tacticalBoardHasContent(raw: unknown): boolean {
  if (raw == null) return false;
  const result = sceneFromOnDisk(raw);
  if (!result.ok) return false;
  return result.value.tokens.length > 0 || result.value.shapes.length > 0;
}
